import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useVisibilityChange, useDebounce } from "@uidotdev/usehooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Input,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
} from '@mui/material';
import {
  CalendarViewMonthOutlined as ScheduleIcon,
  FormatListBulleted as ListIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { toast, Page, EnhancedTable } from 'components';
import { callApi, download } from 'utils';
import { useSession, useMisc } from 'store';
import { t } from 'i18n';
import { GroupSchedule } from './components';

const statusColor = {
  running: '#67bf0e',
  active: '#4c75d7',
  inactive: '#a5a8a2',
  warning: '#df000f',
}

// enum for all action types
const ActionTypes = Object.freeze({
  PAUSE_JOBS: 1,
  RESUME_JOBS: 2,
  DELETE_JOBS: 3,
  SHOW_SCHEDULES: 4,
  SHOW_MOREOPS: 5,
  CLEANUP: 6,
});

const setFormatOrg = (misc) => (row, col) => misc.org[row[col]];
const formatDate = (row, _col) => row.start_date + ' ~ ' + row.end_date;
const setFormatTraffic = (misc) => (row, col) => misc.traffic_curve[row[col]];
const formatStatus = (row, col) => (<span style={{fontSize: '1.5em', fontWeight: 'bold', color: statusColor[row[col]]}}>⦾</span>);
const setFormatAction = (setAction) => (row, _col) => [
  <IconButton onClick={() => setAction([ActionTypes.PAUSE_JOBS, row])} style={{display: row.cnt_all === row.cnt_suspend ? 'none' : undefined}} size="small" key="pause">
    <PauseIcon fontSize="inherit" />
  </IconButton>,
  <IconButton onClick={() => setAction([ActionTypes.RESUME_JOBS, row])} style={{display: row.cnt_suspend === 0 ? 'none' : undefined}} size="small" key="resume">
    <PlayIcon fontSize="inherit" />
  </IconButton>,
  <IconButton onClick={() => setAction([ActionTypes.SHOW_SCHEDULES, row])} size="small" key="schedule">
    <ScheduleIcon fontSize="inherit" />
  </IconButton>,
  <IconButton component={NavLink} to={`/jobs?gid=${row.gid}`} size="small" key="list">
    <ListIcon fontSize="inherit" />
  </IconButton>,
  <IconButton onClick={(ev) => setAction([ActionTypes.SHOW_MOREOPS, row, ev.currentTarget])} size="small" key="moreops">
    <MoreVertIcon fontSize="inherit" />
  </IconButton>
];

const columns = [
  { name: 'gid', label: t('Group ID') },
  { name: 'name', label: t('Group Name') },
  { name: 'org', label: 'Org' },
  { name: 'date', label: t('Start/End Date'), format: formatDate },
  { name: 'traffic_curve', label: t('Traffic') },
  { name: 'cnt_all', label: t('Jobs') },
  { name: 'total_imps', label: t('Total Imps') },
  { name: 'total_clicks', label: t('Total Clicks') },
  { name: 'today_imps', label: t('Today Imps') },
  { name: 'today_clicks', label: t('Today Clicks') },
  { name: 'status', label: t('Status'), format: formatStatus },
  { name: 'action', label: t('Action') },
];

function loopUpdateStatus(active, cb, delay) {
  if (!active)
    return;

  const getAbnGroups = () => callApi(`/jobs/abngroups`).then(res =>
    cb(res?.length ? Object.fromEntries(res.map(gid => [gid, {status: 'warning'}])): null)
  ).catch(console.error);
  getAbnGroups();

  const itvl = setInterval(() => getAbnGroups(), delay);
  return () => clearInterval(itvl);
}

const defaultFilter = {status: 'active', org: '', q: ''};

const GroupList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const documentVisible = useVisibilityChange();
  const session = useSession((state) => state.session);
  const misc = useMisc((state) => state.data);
  const [cols, setCols] = useState([]);
  const [action, setAction] = useState(null);
  const [amend, setAmend] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState([]);

  const filter = Object.fromEntries(
    ['status', 'org', 'q'].map(k => [k, searchParams.get(k) || defaultFilter[k]])
  );

  const q = useDebounce(filter.q, 1500);
  const usp = Object.entries({...filter, q})
    .reduce((pv, [k, v]) => {v && pv.set(k, v); return pv}, new URLSearchParams())
    .toString();
  const dataUrl = `/jobs/groups?${usp}`;

  const isAdmin = session.role === 'admin';
  const isShang = session.uid === 1;

  const updateSearchParam = (k, v) => {
    setSearchParams((prev) => {
      v !== defaultFilter[k] ? prev.set(k, v) : prev.delete(k);
      k !== 'page' && prev.delete('page');
      return prev;
    });
  };

  const clearAction = useCallback(() => setAction(null), []);
  const handleCloseMoreOps = useCallback(() => setTimeout(
    () => setAction(prev => prev?.[0] === ActionTypes.SHOW_MOREOPS ? null : prev),
    50
  ), []);

  useEffect(() => void(setCols(
    columns.filter(col => isAdmin || col.name !== 'org').map(col => {
      let format;
      if (col.name === 'org')           format = setFormatOrg(misc);
      if (col.name === 'traffic_curve') format = setFormatTraffic(misc);
      if (col.name === 'action')        format = setFormatAction(setAction);
      return format ? {...col, format} : col;
    })
  )), [isAdmin, misc]);

  const activeUpdate = documentVisible;
  useEffect(() => loopUpdateStatus(activeUpdate, setAmend, 60_000), [activeUpdate]);

  const reloadJobs = useCallback(() => setRefresh(prev => prev-1), []);

  const onSelectedChange = useCallback((ev) => setSelected((prev) => {
    if (Array.isArray(ev)) return ev;
    const t = ev.target, v = Number(t.value);
    const next = [...prev];
    const i = next.indexOf(v);
    if (t.checked) {
      i === -1 && next.push(v);
    } else {
      i >= 0 && next.splice(i, 1);
    }
    return next;
  }), []);

  let modalType, modalData, dialogContent, moreopsGrp, moreOpsEl;
  if (action) {
    const type = action[0];
    if (type === ActionTypes.PAUSE_JOBS || type === ActionTypes.RESUME_JOBS) {
      const grp = action[1];
      const txt = (type === ActionTypes.PAUSE_JOBS ? 'Suspend' : 'Continue') + ` job group ${grp.name}?`;
      dialogContent = (<DialogContentText>{txt}</DialogContentText>);
    } else if (type === ActionTypes.CLEANUP) {
      const grp = action[1];
      let txt;
      if (grp) {
        txt = `Cleanup job ${grp.gid}: ${grp.name}?`;
      } else {
        txt = `Cleanup selected ${selected?.length} jobs?`;
      }
      dialogContent = (<DialogContentText>{txt}</DialogContentText>);
    } else if (type === ActionTypes.SHOW_MOREOPS) {
      moreopsGrp = action[1];
      moreOpsEl = action[2];
    } else {
      modalType = type;
      modalData = action[1];
    }
  }

  const confirmDialog = () => {
    const [type, grp] = action;
    if (type === ActionTypes.PAUSE_JOBS || type === ActionTypes.RESUME_JOBS) {
      const data = {suspend: type === ActionTypes.PAUSE_JOBS};
      const url = `/jobs/batch/update?gid=${grp.gid}`;
      callApi(url, 'PUT', data).then(() => {
        setAction(null);
        reloadJobs();
      }).catch(console.error);
    } else if (type === ActionTypes.CLEANUP) {
      const ids = grp ? grp.fc_id : selected?.join(',');
      callApi(`/jobs/cleanup?ids=${ids}`).then((res) => {
        console.log(res);
        setAction(null);
        setSelected([]);
        toast(JSON.stringify(res), 'success');
      }).catch(console.error);
    }
  };

  return (
    <Page title='Job Groups'>
      <Toolbar sx={{mt: -1}}>
        <Box sx={{flexGrow: 1}}>
          <ToggleButtonGroup size="small" value={filter.status} exclusive onChange={(_, val) => updateSearchParam('status', val)}>
            <ToggleButton value="active" sx={{padding: '5px'}}>Active</ToggleButton>
            <ToggleButton value="all" sx={{padding: '5px'}}>All</ToggleButton>
          </ToggleButtonGroup>
        {isAdmin && (
          <Select
            name="org"
            value={filter.org}
            size="small"
            onChange={(ev) => updateSearchParam('org', ev.target.value)}
            displayEmpty
            sx={{ ml: 1, '& .MuiSelect-select': {py: '6px'} }}
          >
            <MenuItem value={''}>All Orgs</MenuItem>
          {Object.entries(misc['org']).map(([val, display]) => (
            <MenuItem value={val} key={val}>{display}</MenuItem>
          ))}
          </Select>
        )}
        </Box>
        <Input
          sx={{p: '2px', mr: 1, width: 160}}
          name="q"
          value={filter.q}
          size="small"
          onChange={(ev) => updateSearchParam('q', ev.target.value)}
          inputProps={{ style: {marginLeft: '-5px'} }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Button color="inherit" onClick={reloadJobs}>Reload</Button>
        {isShang && (
        <IconButton color="inherit" onClick={(ev) => setAction([ActionTypes.SHOW_MOREOPS, null, ev.currentTarget])}>
          <MoreVertIcon />
        </IconButton>
        )}
      </Toolbar>
      <Paper sx={{p: 1, pb: 0, m: 2, mt: -1}}>
        <EnhancedTable
          stickyHeader
          columns={cols}
          idColumn="gid"
          dataUrl= {dataUrl}
          multiPage={true}
          updatePageUrl={true}
          dataAmend={amend}
          selected={isShang ? selected : null}
          onSelectedChange={onSelectedChange}
          refresh={refresh}
        />
      </Paper>
      <Menu
        id="globalops-menu"
        anchorEl={moreOpsEl}
        open={Boolean(moreOpsEl) && !moreopsGrp}
        onClose={handleCloseMoreOps}
        onClick={handleCloseMoreOps}
      >
        {isShang && (
        <MenuItem disabled={!selected?.length} onClick={() => setAction([ActionTypes.CLEANUP, null])}>Cleanup Selected</MenuItem>
        )}
      </Menu>
      <Menu
        id="jobops-menu"
        anchorEl={moreOpsEl}
        open={Boolean(moreopsGrp)}
        onClose={handleCloseMoreOps}
        onClick={handleCloseMoreOps}
      >
        <MenuItem onClick={() => download(`/jobs/export?gid=${moreopsGrp?.gid}`, 'jobs')}>{t('Export Order Jobs')}</MenuItem>
        <MenuItem component={NavLink} to={`/reports/jobdaily?idtype=parent&id=${moreopsGrp?.gid}`} target="_blank">{t('Order Daily Report')}</MenuItem>
        <MenuItem component={NavLink} to={`/reports/trackers?idtype=parent&id=${moreopsGrp?.gid}`} target="_blank">{t('Order Track Stats')}</MenuItem>
        {isAdmin && moreopsGrp?.gid === moreopsGrp?.fc_id && (
        <MenuItem onClick={() => setAction([ActionTypes.CLEANUP, moreopsGrp])}>Cleanup</MenuItem>
        )}
      </Menu>
      <GroupSchedule group={modalType === ActionTypes.SHOW_SCHEDULES ? modalData : null} onClose={clearAction} />
    {Boolean(dialogContent) && (
      <Dialog open={true} onClose={clearAction}>
        <DialogContent>
          {dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={clearAction}>Cancel</Button>
          <Button onClick={confirmDialog}>Confirm</Button>
        </DialogActions>
      </Dialog>
    )}
    </Page>
  );
};

export default GroupList;
